import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import {
  MatLegacyTable as MatTable,
  MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';

import * as day from 'dayjs';
import { FlexTimePeriod } from 'libs/domain';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';
import { withDirtyParams } from '@app/src/app/shared/services/filtering/utils/query-processing.utils';

import { FlexTimePermissionsService } from '@shared/services/flex-time/flex-time-permissions';
import { MediaService } from '@shared/services/media';

import { FlexTimeManagerMessages, FlexTimeManagerRoutes } from '../../constants';
import {
  FTM_PERIODS_DISPLAY_COLUMNS,
  FlexPeriodQueryParams,
  FtmPeriodsMessage as FtmPeriodsEditMessages,
  FtmPeriodsMessages,
  MOBILE_BREAKPOINTS,
  PeriodLockingIconSizes,
  PeriodLockingIcons,
  flexPeriodQuerySchema,
} from './constants';
import { FtmPeriodsService } from './services';

@Component({
  selector: 'mg-ftm-periods',
  templateUrl: './ftm-periods.component.html',
  styleUrls: ['./ftm-periods.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QueryParamService],
})
export class FtmPeriodsComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild(MatTable) matTable: MatTable<FlexTimePeriod>;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  /** Constants */
  public readonly FTM_MSG = FlexTimeManagerMessages;
  public readonly MSG = FtmPeriodsMessages;
  public readonly EDIT_MSG = FtmPeriodsEditMessages;
  public readonly PERIOD_LOCKS = PeriodLockingIcons;

  /** General Observables */
  private readonly _destroyed = new ReplaySubject<void>(1);

  private _activeSortColumnSubject = new BehaviorSubject<string>(null);
  public readonly activeSortColumn$ = this._activeSortColumnSubject.asObservable();

  /** Loading */
  private readonly _isLoading = new BehaviorSubject(true);
  public readonly isLoading$ = this._isLoading.asObservable().pipe(shareReplay());

  /** Can Edit */
  private readonly _canEdit = new BehaviorSubject(false);
  public readonly canEdit$ = this._canEdit.asObservable().pipe(takeUntil(this._destroyed), shareReplay());

  /** Search Filter */

  private _hasSearchValue = false;

  /** FlexTime Periods Data */
  private _periods = new BehaviorSubject<FlexTimePeriod[]>([]);
  public readonly periods$ = this._periods.asObservable().pipe(shareReplay());

  /** Table Data */
  public readonly dataSource = new MatTableDataSource<FlexTimePeriod>([]);
  private _dataSub: Subscription;
  public readonly displayedColumns$ = combineLatest([this.media.breakpoint$, this.canEdit$]).pipe(
    takeUntil(this._destroyed),
    map(([breakpoint, canEdit]) => {
      let columns = [...FTM_PERIODS_DISPLAY_COLUMNS];
      if (canEdit) columns = [...columns, 'actions'];
      const displayedColumns = MOBILE_BREAKPOINTS.includes(breakpoint) ? ['mobile'] : columns || [];

      return displayedColumns;
    }),
  );

  /** Empty State */
  get showEmptyState() {
    return this._hasSearchValue ? !this.dataSource?.filteredData?.length : !this.dataSource?.data?.length;
  }

  /** Other */
  public lockIconSize$ = this.media.breakpoint$.pipe(
    map(breakpoint => {
      return MOBILE_BREAKPOINTS.includes(breakpoint) ? PeriodLockingIconSizes.LARGE : PeriodLockingIconSizes.MEDIUM;
    }),
  );

  public queryParams$: Observable<FlexPeriodQueryParams> = this._queryService.useQueryParams(
    flexPeriodQuerySchema.parse,
  );

  /** Component Constructor */
  constructor(
    public flexPeriods: FtmPeriodsService,
    public media: MediaService,
    private _cdr: ChangeDetectorRef,
    private _fb: UntypedFormBuilder,
    private _flexPermissionsService: FlexTimePermissionsService,
    protected authInfo: AuthInfoService,
    private _queryService: QueryParamService<FlexPeriodQueryParams>,
  ) {
    this._canEdit.next(this._flexPermissionsService.isFlexTimeAdmin());
    this._dataSub = this.flexPeriods.periods$.pipe(takeUntil(this._destroyed)).subscribe(periods => {
      this.dataSource.data = periods;
      this._cdr.markForCheck();
    });
  }

  ngOnInit(): void {
    this.queryParams$.pipe(withDirtyParams()).subscribe(({ params, dirty }) => {
      if (dirty.start_date || dirty.end_date) {
        this._fetchFlexPeriods(params.start_date, params.end_date);
      }
      this._filterClientSide({ title: params.title });
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.sort.sort({
      id: 'date',
      start: 'asc',
      disableClear: false,
    });
    this.dataSource.filterPredicate = this._tableFilter;
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
    this._canEdit.complete();
    this._dataSub.unsubscribe();
  }

  public clone(id: number): void {
    this.flexPeriods.openModal(id, true);
  }

  public async goToActivities(period: FlexTimePeriod, queryParams: FlexPeriodQueryParams): Promise<void> {
    this._queryService.updateQueryParams<any>(
      {
        start_date: queryParams.start_date,
        end_date: queryParams.end_date,
        period_id: period.id,
      },
      {},
      [`/${FlexTimeManagerRoutes.ROOT}`, FlexTimeManagerRoutes.ACTIVITIES],
    );
  }

  public async openMyActivityEdit(period: FlexTimePeriod, queryParams: FlexPeriodQueryParams): Promise<void> {
    const { id } = period;

    const myInstance = period.activityInstances.find(
      a => a.flexTimeActivity?.createdByPerson.hash === this.authInfo.authPersonHash,
    );
    const currentInstanceId = myInstance ? myInstance.id : null;
    this.flexPeriods.openActivityEditModal(id, queryParams.start_date, queryParams.end_date, currentInstanceId, true);
  }

  public trackById(index: number, item: FlexTimePeriod) {
    return item ? item.id : index;
  }

  public isInPast(data: FlexTimePeriod): boolean {
    const date = day(data.date).format('MMM DD YYYY');
    const parsed = `${date} ${data.endTime}`;

    const periodDate = day(parsed, 'MMM DD YYYY HH:mm:ss');

    return periodDate.isBefore(day(), 'minute');
  }

  public selectActiveColumn(columnKey: string) {
    this._activeSortColumnSubject.next(columnKey);
  }

  public rangeChanged(range) {
    this._queryService.updateQueryParams({
      start_date: range.start,
      end_date: range.end,
    });
  }

  public titleChange(title: string) {
    this._queryService.updateQueryParams({
      title,
    });
  }

  private _tableFilter(period: FlexTimePeriod, titleString: string) {
    if (!titleString.length) return true;
    if (!titleString || period.title.toLowerCase().includes(titleString)) return true;
    return false;
  }

  private async _fetchFlexPeriods(start: day.Dayjs, end: day.Dayjs): Promise<void> {
    this._isLoading.next(true);
    await this.flexPeriods.fetchAll(start, end);
    this._isLoading.next(false);
  }

  private async _filterClientSide({ title }) {
    const trimmedTitle = title.trim().toLowerCase() || '';
    this._hasSearchValue = !!trimmedTitle;
    this.dataSource.filter = trimmedTitle;
    this._cdr.markForCheck();
  }
}
